import * as React from 'react';

/**
 * iPhone-style page slide. Attach the returned ref to the container whose
 * content changes with `current`: moving forward through `order` eases the new
 * page in from the right, going back eases it in from the left.
 *
 * Driven by the Web Animations API rather than a React key so children keep
 * their state (scroll position, open panels) instead of remounting. Skipped
 * for `prefers-reduced-motion`.
 */
export function usePageSlide<T extends string, E extends HTMLElement = HTMLDivElement>(
  order: readonly T[],
  current: T,
  enabled = true,
) {
  const ref = React.useRef<E | null>(null);
  const prevIndex = React.useRef(order.indexOf(current));

  React.useLayoutEffect(() => {
    const el = ref.current;
    const idx = order.indexOf(current);
    const prev = prevIndex.current;
    prevIndex.current = idx;
    if (!enabled || !el || idx < 0 || prev < 0 || idx === prev) return;
    if (typeof el.animate !== 'function') return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches) return;

    // The page starts a full width off-screen, which would widen any scrollable
    // ancestor into a horizontal scroll for the duration. Clip them while it
    // runs — including <html>, since on desktop the document itself scrolls.
    const clipped: { el: HTMLElement; prev: string }[] = [];
    const clip = (node: HTMLElement) => {
      clipped.push({ el: node, prev: node.style.overflowX });
      node.style.overflowX = 'hidden';
    };
    for (let p: HTMLElement | null = el; p && clipped.length < 4; p = p.parentElement) {
      const ox = window.getComputedStyle(p).overflowX;
      if (ox === 'auto' || ox === 'scroll') clip(p);
    }
    clip(document.documentElement);
    const restore = () => { for (const c of clipped) c.el.style.overflowX = c.prev; };

    const from = idx > prev ? '100%' : '-100%';
    const anim = el.animate(
      [
        { transform: `translate3d(${from}, 0, 0)`, opacity: 0.4 },
        { transform: 'translate3d(0, 0, 0)', opacity: 1 },
      ],
      { duration: 280, easing: 'cubic-bezier(0.22, 0.61, 0.36, 1)' },
    );
    anim.addEventListener('finish', restore);
    anim.addEventListener('cancel', restore);
    return () => { anim.cancel(); restore(); };
    // `order` is often an inline array (new identity each render); `current` is
    // the only real trigger, and the index guard above handles reordering.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current, enabled]);

  return ref;
}
